/**
 * 工资条的 **「子项」层** —— 用户看得见的是「一项」，账本落的是「一条或两条腿」。
 *
 * 依据：`DR-016` §3（工资条结构定稿）· C 域 PRD §5.2.1（十条铁律）· `DR-052`（F3 只读此结构）
 *
 * ## 🔴 为什么需要有这一层（这是本批的核心）
 *
 * 改前工资条的录入单位是**腿**：一个「转账子项」（医保 400）要用户自己录两条
 * —— `工资卡 out 400` + `社保账户 in 400`（`DR-016` §3.3 的腿 4）。
 * 于是 `7 个子项` 实际要录 `9~10 行`，每行 6 个控件。用户说"记起来太复杂"，
 * 根因不在控件多，而在**记账模型直接暴露给了用户**。
 *
 * 本文件把「子项 → 腿」的展开收在**一处纯函数**里：界面只认 `SalaryItem`，
 * 提交前调 {@link expandSalaryItems} 一次性摊成腿，**再**交给 `buildRequest`（唯一拼装处）。
 *
 * | 层 | 谁在用 | 是否落库 |
 * |---|---|---|
 * | `SalaryItem`（项） | 录入界面 / 编辑态回填 | ❌ 不落库（派生自腿） |
 * | `DraftLeg`（腿） | `buildRequest` → `checkLegShape` → 服务端 | ✅ |
 *
 * ## 🔴 口径一条没改
 *
 * | 铁律（C 域 §5.2.1） | 在本文件里怎么落 |
 * |---|---|
 * | ① 父项 = 实发，Σ子项（代数和）= 父项 | 父项是**派生值**（腿里没有"金额"列）⇒ 展开后自动成立 |
 * | ② 收入按税前计 | `income` 项 → 主账户 `in` 腿 |
 * | ③ 企业缴费独立成笔 | 本文件**不生成**"单位缴纳" —— 它不属于工资条（§3.4） |
 * | ⑥ 去向自由、出口唯一 | 主账户由调用方传入且恒定；去向 = 分类或目标账户，完全自由 |
 * | ⑦ 子项自由增删 | `SalaryItem[]` 就是自由列表，无固定模板 |
 * | ⑧ 类型三选一、方向由类型定 | 用户只选 `income` / `expense` / `transfer`，**不选方向** |
 * | ⑨ 上限 15 | 见 {@link validateSalaryItems} —— 🔴 **按展开后的腿数**判（见下） |
 * | ⑩ 到账金额只作录入期对账 | `netText` 不在这里、也不进请求（由页面持有） |
 *
 * ## 🔴 「上限 15」为什么按腿数判，而不是按项数判
 *
 * `DR-016` §3.8④ 写的是「子项上限 15」，但内核 `checkCompositeShape()` 是**按腿数**拦
 * （`MaxPayrollItems = 15`，见 `nucleus/split.ts` 的注释：精确的"子项 ≤ 15"由录入界面保证）。
 * 一个转账子项 = 2 条腿 ⇒ 若端侧只数"项"，用户看到 8 项就点保存，会被服务端
 * 用「跨账户的多腿交易上限为 15 条」拦下 —— 那句话对他毫无意义（他只填了 8 项）。
 * ⇒ 端侧**提前按腿数**判，并在文案里说清"哪来的 15"。
 */

import {
  AccountType,
  CAT,
  InternalCategory,
  LegDirection,
  MaxPayrollItems,
  centsToYuanText,
  toCents,
  type AccountView,
  type Cents,
  type CategoryView,
  type TransactionLegView,
} from '@jiaying/shared';

import { yuanTextToCentsOrNull, type DraftLeg } from './txn-draft.js';

/**
 * 子项的三种类型（`DR-016` §3.8②：**方向由类型决定，用户不选方向**）。
 *
 * | 类型 | 主账户腿 | 对手方 | 计入收支 |
 * |---|---|---|---|
 * | `income` | `in` + 收入分类 | —— | ✅ |
 * | `expense` | `out` + 支出分类 | —— | ✅ |
 * | `transfer` | `out`（无分类） | 目标账户 `in` | ❌ 两腿都不计 |
 */
export type SalaryItemType = 'income' | 'expense' | 'transfer';

/**
 * 展示分组（**只影响排版**，不影响账务）。
 *
 * `gross` 应发 · `deduct` 代扣 · `after` 税后去向。
 * ⚠️ 编辑一笔历史工资条时，`deduct` 与 `after` **无法从腿反推**
 * （两者在账上都是"从工资卡流出"）⇒ {@link salaryItemsFromLegs} 一律归 `deduct`。
 * 这不影响金额：分组只是标题。
 */
export type SalaryItemGroup = 'gross' | 'deduct' | 'after';

/** 一个工资条子项（**用户输入态**，金额是文本、未换算） */
export interface SalaryItem {
  /** 仅 React key 用，不参与账务、不落库 */
  readonly id: string;
  /** 子项名（可改，`DR-016` §3.8①） */
  readonly name: string;
  readonly group: SalaryItemGroup;
  readonly type: SalaryItemType;
  /** 十进制字面量；空串 = **这一项不记**（见 {@link expandSalaryItems}） */
  readonly amountText: string;
  /** `income` / `expense` 的去向分类；`transfer` 恒为 `null` */
  readonly categoryId: string | null;
  /** `transfer` 的目标账户；其余类型恒为 `null` */
  readonly targetAccountId: string | null;
  /**
   * 腿级「不计收支」的**显式覆盖**。
   *
   * - `transfer`：**恒为不计**（`buildRequest` 前由本文件强制 `true`），此字段被忽略
   * - `income` / `expense`：缺省 = 计入；只有"社保账户自己计不计"这类极端场景才需要覆盖
   *
   * 🔴 值 `undefined` = 跟随类型推导，**不是** `false`（铁律 107：两种缺失不能混）。
   */
  readonly excludeFromStats?: boolean;
}

// ═════════════════════ 默认结构（`DR-016` §3.8① 的起点） ═════════════════════

/**
 * 新建工资条时的**默认子项**（只是起点，每条都可删 / 改名 / 改金额）。
 *
 * ## 为什么是这 5 项、不是 DR-016 表里的 7 项
 *
 * §3.8① 的默认表有 7 行（含「各类补贴」与「税后去向」）。本文件只给**最普遍**的 5 项，
 * 因为「补贴 / 年终奖 / 房贷代扣」**各单位差异极大** —— 给了空白行，用户还得判断
 * "这行要不要填"，反而变成新负担。「+ 添加一项」随时能加（§3.8① 明确可加）。
 *
 * ## 🔴 目标账户与分类都**从数据里找**，不硬编名字
 *
 * - 医保 / 公积金按 **`InternalCategory` 稳定枚举**匹配（不是字符串 `includes('医保')`）——
 *   分类与账户都可以改名，枚举不会（`DR-016` §10.1 已把"补充养老 / 企业年金"合并，改名是常态）
 * - 分类只在 `GET /api/categories` **真有这个 id** 时才预填（用户可能已经移除该分类，
 *   填一个不存在的 id 会让选择器显示空白 —— 那是"看起来填了其实没填"）
 * - 找不到就留空（`null`），由用户自己选；**不猜**
 */
export function defaultSalaryItems(
  accounts: readonly Pick<AccountView, 'id' | 'type' | 'category'>[],
  categories: readonly Pick<CategoryView, 'id'>[],
): SalaryItem[] {
  const hasCat = (id: string): boolean => categories.some((c) => c.id === id);
  const internalAccount = (category: InternalCategory): string | null => {
    const hit = accounts.find(
      (a) => a.type === AccountType.Internal && a.category === category,
    );
    return hit === undefined ? null : hit.id;
  };

  const salaryCat = hasCat(CAT.SALARY) ? CAT.SALARY : null;
  const socialCat = hasCat(CAT.SOCIAL_INSURANCE) ? CAT.SOCIAL_INSURANCE : null;
  const taxCat = hasCat(CAT.TAX) ? CAT.TAX : null;

  return [
    {
      id: 'salary-gross',
      name: '税前工资',
      group: 'gross',
      type: 'income',
      amountText: '',
      categoryId: salaryCat,
      targetAccountId: null,
    },
    {
      id: 'salary-pension',
      name: '养老',
      group: 'deduct',
      type: 'expense',
      amountText: '',
      categoryId: socialCat,
      targetAccountId: null,
    },
    {
      id: 'salary-medical',
      name: '医保',
      group: 'deduct',
      type: 'transfer',
      amountText: '',
      categoryId: null,
      targetAccountId: internalAccount(InternalCategory.SocialMedical),
    },
    {
      id: 'salary-fund',
      name: '公积金',
      group: 'deduct',
      type: 'transfer',
      amountText: '',
      categoryId: null,
      targetAccountId: internalAccount(InternalCategory.SocialFund),
    },
    {
      id: 'salary-tax',
      name: '个税',
      group: 'deduct',
      type: 'expense',
      amountText: '',
      categoryId: taxCat,
      targetAccountId: null,
    },
  ];
}

/** 新增一项时的初值（默认落在代扣组、类型 = 支出） */
export function emptySalaryItem(id: string, group: SalaryItemGroup = 'deduct'): SalaryItem {
  return {
    id,
    name: group === 'gross' ? '补贴' : '新的一项',
    group,
    type: group === 'gross' ? 'income' : 'expense',
    amountText: '',
    categoryId: null,
    targetAccountId: null,
  };
}

/** 切换类型时必须清掉**另一条通路**的去向（否则会留下一个用户看不见却会提交的值） */
export function withType(item: SalaryItem, type: SalaryItemType): SalaryItem {
  return {
    ...item,
    type,
    categoryId: type === 'transfer' ? null : item.categoryId,
    targetAccountId: type === 'transfer' ? item.targetAccountId : null,
  };
}

// ═════════════════════ 合计（只用于展示与对账） ═════════════════════

export interface SalaryTotals {
  /** 应发（`gross` 组的收入项） */
  readonly grossCents: Cents;
  /** 代扣（`deduct` 组的支出 + 转账项） */
  readonly deductCents: Cents;
  /** 税后去向（`after` 组） */
  readonly afterCents: Cents;
  /** 实发 = 应发 − 代扣 − 税后去向（= 父项，`DR-016` §3.1） */
  readonly netCents: Cents;
  /** 真正有金额的项数（空金额 = 这一项没记） */
  readonly usedCount: number;
}

/**
 * 逐组求和（整数分）。
 *
 * 🔴 **空金额项跳过、非法金额按 0 计**：非法格式由 {@link validateSalaryItems} 报，
 * 这里不重复判 —— 否则同一个错误会在"合计"和"校验"里各说一句（用户看到两条一样的错）。
 */
export function salaryTotals(items: readonly SalaryItem[]): SalaryTotals {
  let gross = 0;
  let deduct = 0;
  let after = 0;
  let used = 0;
  for (const item of items) {
    const cents = yuanTextToCentsOrNull(item.amountText);
    if (cents === null || cents <= 0) continue;
    used += 1;
    if (item.group === 'gross') gross += cents;
    else if (item.group === 'after') after += cents;
    else deduct += cents;
  }
  return {
    grossCents: gross as Cents,
    deductCents: deduct as Cents,
    afterCents: after as Cents,
    netCents: (gross - deduct - after) as Cents,
    usedCount: used,
  };
}

// ═════════════════════ 校验 ═════════════════════

/**
 * 提交前校验（**只报用户能改的事**，不重复内核的形态规则）。
 *
 * 🔴 内核（`checkLegShape`）仍会在 `buildRequest` 里跑一遍 —— 本函数是"提前说人话"，
 * **不是**第二份形态判定。这里只判：金额格式 / 必填去向 / 腿数上限。
 */
export function validateSalaryItems(
  items: readonly SalaryItem[],
  mainAccountId: string,
): string[] {
  const errors: string[] = [];
  if (mainAccountId === '') {
    errors.push('先选工资到账的账户 —— 整笔工资都从这张卡进出');
  }

  for (const item of items) {
    const text = item.amountText.trim();
    if (text === '') continue; // 没填 = 这一项不记（见 expandSalaryItems）
    const cents = yuanTextToCentsOrNull(text);
    if (cents === null) {
      errors.push(`「${item.name}」金额格式不对（最多两位小数，不要带负号）`);
      continue;
    }
    if (cents <= 0) {
      errors.push(`「${item.name}」金额要大于 0 —— 方向由「收入 / 支出 / 转账」决定，不用填负数`);
      continue;
    }
    if (item.type === 'transfer') {
      if (item.targetAccountId === null || item.targetAccountId === '') {
        errors.push(`「${item.name}」要选转到哪个账户`);
      } else if (item.targetAccountId === mainAccountId) {
        errors.push(`「${item.name}」不能转回工资到账的那张卡（同一账户一出一进等于没记）`);
      }
    } else if (item.categoryId === null || item.categoryId === '') {
      errors.push(
        `「${item.name}」要选一个分类 —— ${item.type === 'income' ? '收入' : '支出'}进报表要靠分类`,
      );
    }
  }

  const totals = salaryTotals(items);
  if (totals.usedCount === 0 && errors.length === 0) {
    errors.push('至少填一项（税前工资或其它构成），否则这笔工资没有内容');
  }

  /**
   * 🔴 腿数上限按**展开后**数（见文件头）。转账一项 = 2 腿 ⇒ 上限实际落在"约 8 项"。
   * 文案必须给出**可执行的下一步**（合并），而不是只说"超了"。
   */
  const legCount = expandSalaryItems(items, mainAccountId).length;
  if (legCount > MaxPayrollItems) {
    errors.push(
      `这些项会展开成 ${String(legCount)} 条记账条目，超过单笔工资条的上限 ${String(MaxPayrollItems)} 条 ——`
        + ' 转账类（医保 / 公积金）每项占 2 条，请把同类合并成一项（如养老与失业合成「社保」）',
    );
  }

  return errors;
}

// ═════════════════════ 展开：项 → 腿 ═════════════════════

/**
 * 把子项摊成腿（**唯一展开处**）。
 *
 * | 类型 | 产出 |
 * |---|---|
 * | `income` | 1 条：主账户 `in` + 收入分类 |
 * | `expense` | 1 条：主账户 `out` + 支出分类 |
 * | `transfer` | 2 条：主账户 `out`（无分类、不计收支）＋ 目标账户 `in`（无分类、不计收支） |
 *
 * 🔴 **空金额 / 非法金额的项直接跳过**：空 = 用户没填 = 不记（与"删掉"等价，但少一步点击）。
 * 非法金额也会被跳过 —— 但 {@link validateSalaryItems} 会先拦下，走不到这里。
 *
 * 🔴 转账两腿**都**标 `excludeFromStats: true`（`DR-016` §3.3 腿 4/5）：
 * 钱从工资卡挪到自己的社保账户，**净资产不变、收支不变**。
 */
export function expandSalaryItems(
  items: readonly SalaryItem[],
  mainAccountId: string,
): DraftLeg[] {
  const legs: DraftLeg[] = [];
  for (const item of items) {
    const cents = yuanTextToCentsOrNull(item.amountText);
    if (cents === null || cents <= 0) continue;
    const amountText = item.amountText.trim();

    if (item.type === 'transfer') {
      legs.push({
        accountId: mainAccountId,
        direction: LegDirection.Out,
        amountText,
        categoryId: null,
        excludeFromStats: true,
      });
      legs.push({
        accountId: item.targetAccountId ?? '',
        direction: LegDirection.In,
        amountText,
        categoryId: null,
        excludeFromStats: true,
      });
      continue;
    }

    legs.push({
      accountId: mainAccountId,
      direction: item.type === 'income' ? LegDirection.In : LegDirection.Out,
      amountText,
      categoryId: item.categoryId,
      // 只有显式覆盖才写入；`undefined` 表示"跟随默认（计入）"
      excludeFromStats: item.excludeFromStats === true ? true : undefined,
    });
  }
  return legs;
}

// ═════════════════════ 折叠：腿 → 项（编辑态） ═════════════════════

/** 折叠时用于**起名**的最小投影（纯展示，不影响账务；缺省也能工作） */
export interface SalaryFoldContext {
  readonly accounts?: readonly { readonly id: string; readonly name: string }[];
  readonly categories?: readonly { readonly id: string; readonly name: string }[];
}

type FoldLeg = Pick<
  TransactionLegView,
  'accountId' | 'direction' | 'amount' | 'categoryId' | 'excludeFromStats'
>;

/**
 * 把一笔**已存在的工资条**折回子项列表（编辑路径）。
 *
 * ## 算法（只有三步）
 *
 * 1. **主账户** = 同时有 `in` 与 `out` 腿的那个账户（`checkFundSingleOutlet` 的同一判据）。
 *    找不到、或找到两个 ⇒ 返回 `null`（这笔不是工资条形态，交给调用方回退到逐腿编辑）。
 * 2. 主账户的每条 `in` 腿 ⇒ 一个 `income` 项。
 * 3. 主账户的每条 `out` 腿：
 *    - 「不计收支且无分类」⇒ 去**其它账户的 `in` 腿**里找**金额相等**的一条配对 ⇒ `transfer` 项
 *    - 其余 ⇒ `expense` 项
 * 4. 只要有**任何一条腿没被消费掉** ⇒ 返回 `null`（宁可回退，也不静默丢钱）。
 *
 * ## 🔴 为什么必须保证"往返等价"
 *
 * `legs` 在服务端是**整组替换**（`DR-042` D1）⇒ 折叠丢了任一字段，用户"原样保存"
 * 就会把那一项**改成别的数**。⇒ `salary-items.spec.ts` 里有一条硬断言：
 * `expand(fold(legs))` 与 `legs` **逐腿等价**（账户 / 方向 / 金额 / 分类 / 是否不计收支）。
 * 任何一条不成立 ⇒ 折叠就不该被启用（返回 `null` 更诚实）。
 *
 * ## ⚠️ 分组无法反推
 *
 * `deduct` 与 `after` 在账上完全同形（都从工资卡流出）⇒ 折叠一律归 `deduct`。
 * 影响**仅限标题**（金额与去向都不变）。
 */
export function salaryItemsFromLegs(
  legs: readonly FoldLeg[],
  ctx: SalaryFoldContext = {},
): SalaryItem[] | null {
  if (legs.length < 2) return null;

  const byAccount = new Map<string, { ins: FoldLeg[]; outs: FoldLeg[] }>();
  for (const leg of legs) {
    const g = byAccount.get(leg.accountId) ?? { ins: [], outs: [] };
    if (leg.direction === LegDirection.In) g.ins.push(leg);
    else g.outs.push(leg);
    byAccount.set(leg.accountId, g);
  }

  const outlets = [...byAccount.entries()].filter(([, g]) => g.ins.length > 0 && g.outs.length > 0);
  if (outlets.length !== 1) return null;

  const [mainId, mainGroup] = outlets[0] as [string, { ins: FoldLeg[]; outs: FoldLeg[] }];

  /** 非主账户的 `in` 腿池（转账目标），配对时按金额取、用完即删 */
  const targetPool: { accountId: string; leg: FoldLeg }[] = [];
  for (const [accountId, g] of byAccount) {
    if (accountId === mainId) continue;
    for (const leg of g.ins) targetPool.push({ accountId, leg });
  }

  const catName = (id: string | null): string | null => {
    if (id === null) return null;
    return ctx.categories?.find((c) => c.id === id)?.name ?? null;
  };
  const accName = (id: string): string =>
    ctx.accounts?.find((a) => a.id === id)?.name ?? '该账户';

  const items: SalaryItem[] = [];
  const push = (item: Omit<SalaryItem, 'id'>): void => {
    items.push({ ...item, id: `fold-${String(items.length)}` });
  };

  for (const leg of mainGroup.ins) {
    push({
      name: catName(leg.categoryId) ?? '收入',
      group: 'gross',
      type: 'income',
      amountText: centsToYuanText(toCents(leg.amount)),
      categoryId: leg.categoryId,
      targetAccountId: null,
      excludeFromStats: leg.excludeFromStats === true ? true : undefined,
    });
  }

  for (const leg of mainGroup.outs) {
    const isTransferLeg = leg.excludeFromStats === true && leg.categoryId === null;
    if (isTransferLeg) {
      const idx = targetPool.findIndex((t) => t.leg.amount === leg.amount);
      if (idx < 0) return null; // 配不上对手腿 ⇒ 不猜，交回逐腿编辑
      const [hit] = targetPool.splice(idx, 1);
      if (hit === undefined) return null;
      push({
        name: accName(hit.accountId),
        group: 'deduct',
        type: 'transfer',
        amountText: centsToYuanText(toCents(leg.amount)),
        categoryId: null,
        targetAccountId: hit.accountId,
      });
      continue;
    }
    push({
      name: catName(leg.categoryId) ?? '支出',
      group: 'deduct',
      type: 'expense',
      amountText: centsToYuanText(toCents(leg.amount)),
      categoryId: leg.categoryId,
      targetAccountId: null,
      excludeFromStats: leg.excludeFromStats === true ? true : undefined,
    });
  }

  // 🔴 有腿没被消费 ⇒ 折不全（典型：某账户只有 out 没有 in 的"外部代扣"）
  if (targetPool.length > 0) return null;

  return items;
}

/**
 * 折叠的前提：这笔交易里有**唯一**的一个"同时收付"的账户（资金出口）。
 *
 * 编辑页在渲染前用一次：不成立就**不启用清单界面**（回退到逐腿编辑），
 * 而不是先渲染一张会丢数据的清单。
 */
export function mainAccountOfLegs(legs: readonly FoldLeg[]): string | null {
  const byAccount = new Map<string, { hasIn: boolean; hasOut: boolean }>();
  for (const leg of legs) {
    const g = byAccount.get(leg.accountId) ?? { hasIn: false, hasOut: false };
    if (leg.direction === LegDirection.In) g.hasIn = true;
    else g.hasOut = true;
    byAccount.set(leg.accountId, g);
  }
  const outlets = [...byAccount.entries()].filter(([, g]) => g.hasIn && g.hasOut);
  return outlets.length === 1 ? (outlets[0]?.[0] ?? null) : null;
}
